import { CircleHelp } from 'lucide-react';
import type { ReactNode } from 'react';
import { Tooltip } from './Tooltip.tsx';

/**
 * Inline "what does this word mean" affordance — a question mark that
 * carries a term's definition in a Radix tooltip.
 *
 * The supply vocabulary this console runs on (namespace / upstream /
 * upstream channel / capability) is a four-level hierarchy that nothing on
 * the page spells out, so column headers read as synonyms to anyone who has
 * not been told otherwise. The definition therefore travels WITH the label
 * rather than living in a doc: an operator who misreads "upstream channel"
 * as "namespace" binds traffic to the wrong lane.
 *
 * It is a real `<button>`, not a hover-only span: Radix opens the tooltip on
 * focus too, so the copy is reachable by keyboard and announced by screen
 * readers through `aria-label`.
 */
interface Props {
  readonly content: ReactNode;
  /** Accessible name — pass a translated string, never a bare glyph. */
  readonly label: string;
  readonly side?: 'top' | 'bottom' | 'left' | 'right';
  readonly testId?: string;
}

export function InfoHint({ content, label, side = 'top', testId }: Props): JSX.Element {
  return (
    <Tooltip content={content} side={side}>
      <button
        type="button"
        aria-label={label}
        data-testid={testId}
        className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full align-middle text-paper-low transition-colors duration-120 hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40"
      >
        <CircleHelp aria-hidden="true" className="h-3.5 w-3.5" />
      </button>
    </Tooltip>
  );
}
